/**
 * Bidding handlers extracted from `useForm/index.tsx`.
 *
 * Owns the first-round and second-round bidding flows that call the
 * `fetchLeadBuyers` API after a lead has been submitted, plus the
 * `fetchFirstRoundBuyers` helper used by the one-to-one flow.
 *
 * The handlers preserve the original behavior verbatim — they are pure
 * wrappers around the same `sendDataToServer` / dispatch / loading
 * helpers that previously lived inline in the parent hook.
 */

import { useMemo } from "react";

import { ActionTypes, FormReducerAction, Buyer } from "../types";
import { SKIPPED_OPERATION_ID_KEY } from "@lib/sharedUtils";
import { fetchLeadBuyers } from "src/api/buyers";
import type { SubmitLeadFormResponse } from "src/api/submitLead";
import type { DomainForm } from "src/api";

const SKIP_ROUND_ONE_CATEGORIES = ["auto-warranty", "car-warranty"];

interface UseBiddingParams {
    form: DomainForm;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    domain: any;
    dispatch: (action: FormReducerAction) => void;
    sendDataToServer: (args: {
        submit: boolean;
        buyerIds?: (number | string)[];
        consentLanguage?: string;
        hasDomainConsent?: boolean;
    }) => Promise<SubmitLeadFormResponse>;
    setLoading: (loading: boolean) => void;
    setLoadingBuyers: (loading: boolean) => void;
    handleRejectedLeads: () => void;
    categorySlug: string | undefined;
    initialFieldData?: { [key: string]: string };
}

export const useBidding = ({
    form,
    domain,
    dispatch,
    sendDataToServer,
    setLoading,
    setLoadingBuyers,
    handleRejectedLeads,
    categorySlug,
    initialFieldData,
}: UseBiddingParams) => {
    // Round-one bidding is skipped for warranty categories — those flows
    // go straight to round two after lead submission.
    const skipRoundOneBidding = useMemo(
        () => !SKIP_ROUND_ONE_CATEGORIES.includes(categorySlug as string),
        [categorySlug],
    );

    const fetchFirstRoundBuyers = (id: string) => {
        setLoadingBuyers(true);
        void fetchLeadBuyers(id).then((res) => {
            if (res.data?.buyers && res.data.buyers.length) {
                dispatch({
                    type: ActionTypes.SET_OPERATION,
                    payload: {
                        firstRoundBuyers: res.data.buyers.map((buyer) => ({
                            ...buyer,
                            selected: true,
                        })),
                        secondRoundBuyers: [],
                        loadingBuyers: false,
                        hasDomainConsent: res.data.buyers.every(
                            (buyer) => buyer.isNoble,
                        )
                            ? false
                            : true,
                    },
                });
            } else {
                dispatch({
                    type: ActionTypes.SET_OPERATION,
                    payload: {
                        loadingBuyers: false,
                    },
                });
            }
            dispatch({
                type: ActionTypes.SET_GLOBAL_LOADING,
                payload: false,
            });
        });
    };

    const startFirstRoundBidding = ({
        operationId,
        firstRoundBuyers,
        secondRoundBuyers,
        isOtpResolved,
    }: {
        operationId: string | null;
        firstRoundBuyers?: Buyer[];
        secondRoundBuyers?: Buyer[];
        isOtpResolved?: boolean;
    }) => {
        if (!operationId) {
            return;
        }
        if (skipRoundOneBidding) {
            sessionStorage.setItem(SKIPPED_OPERATION_ID_KEY, operationId);
            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id, isOtpResolved },
            });
            return;
        }
        if (secondRoundBuyers && secondRoundBuyers.length) {
            dispatch({
                type: ActionTypes.SET_OPERATION,
                payload: {
                    key: "ROUND_TWO",
                    secondRoundBuyers,
                    formErrors: 0,
                },
            });
            return;
        }
        setLoadingBuyers(true);
        if (firstRoundBuyers && firstRoundBuyers.length) {
            void fetchLeadBuyers(operationId)
                .then((res) => {
                    //? SALE IS MADE ... GET THE WINNER BUYER TO SEND IT TO THANK YOU PAGE TO EXCLUDE IT FROM OFFER
                    if (
                        res.data?.status === "SUCCESS" &&
                        res.data.buyers.length > 0
                    ) {
                        dispatch({
                            type: ActionTypes.SET_OPERATION,
                            payload: {
                                winnerBuyer: res.data?.buyers?.map((buyer) => ({
                                    ...buyer,
                                    selected: true,
                                })),
                            },
                        });
                    }
                    if (
                        res.data?.status === "FAILED" &&
                        res.data?.buyers?.length > 0
                    ) {
                        dispatch({
                            type: ActionTypes.SET_OPERATION,
                            payload: {
                                key: "ROUND_TWO",
                                secondRoundBuyers: res.data.buyers.map(
                                    (buyer) => ({
                                        ...buyer,
                                        selected: true,
                                    }),
                                ),
                                formErrors: 0,
                            },
                        });
                    } else if (
                        res.data?.status === "FAILED" &&
                        res.data?.buyers?.length === 0
                    ) {
                        if (
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            domain.fallbackSettings &&
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            domain.fallbackSettings?.domain &&
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            domain.fallbackSettings?.excludedBuyers &&
                            !initialFieldData &&
                            res.data.extraData &&
                            res.data.extraData.redirect
                        ) {
                            setLoading(true);
                            handleRejectedLeads();
                        } else {
                            dispatch({
                                type: ActionTypes.SET_FORM_SUBMITTED,
                                payload: { formId: form.id, isOtpResolved },
                            });
                        }
                        //? SALE IS MADE ... NO BUYER TOOK THE LEAD
                        dispatch({
                            type: ActionTypes.SET_OPERATION,
                            payload: {
                                winnerBuyer: [],
                            },
                        });
                    } else {
                        dispatch({
                            type: ActionTypes.SET_FORM_SUBMITTED,
                            payload: { formId: form.id, isOtpResolved },
                        });
                    }
                })
                .finally(() => {
                    setLoadingBuyers(false);
                });
        } else {
            dispatch({
                type: ActionTypes.SET_FORM_SUBMITTED,
                payload: { formId: form.id, isOtpResolved },
            });
        }
    };

    const startSecondRoundBidding = async ({
        selectedBuyers,
        consentLanguage,
        hasDomainConsent,
    }: {
        selectedBuyers: Buyer[];
        consentLanguage?: string;
        hasDomainConsent?: boolean;
    }) => {
        setLoading(true);
        const { success, data } = await sendDataToServer({
            submit: true,
            buyerIds: selectedBuyers.map((buyer) => buyer.buyerId),
            consentLanguage,
            hasDomainConsent,
        });

        if (!success) {
            setLoading(false);
            dispatch({
                type: ActionTypes.SET_FORM_ERROR,
                payload: "An Unexpected Error Occurred",
            });
        } else {
            if (data?.operationId) {
                void fetchLeadBuyers(data?.operationId)
                    .then((res) => {
                        if (
                            res.data?.status === "FAILED" &&
                            res.data?.buyers?.length > 0
                        ) {
                            dispatch({
                                type: ActionTypes.SET_OPERATION,
                                payload: {
                                    key: "ROUND_TWO",
                                    secondRoundBuyers: res.data.buyers.map(
                                        (buyer) => ({
                                            ...buyer,
                                            selected: true,
                                        }),
                                    ),
                                    formErrors: 0,
                                },
                            });
                        } else {
                            dispatch({
                                type: ActionTypes.SET_FORM_SUBMITTED,
                                payload: { formId: form.id },
                            });
                        }
                    })
                    .finally(() => {
                        setLoadingBuyers(false);
                        setLoading(false);
                    });
            } else {
                dispatch({
                    type: ActionTypes.SET_FORM_SUBMITTED,
                    payload: { formId: form.id },
                });
            }
        }
    };

    return {
        fetchFirstRoundBuyers,
        startFirstRoundBidding,
        startSecondRoundBidding,
    };
};
